import { defineStore } from 'pinia';
import { PaymentServices as _paymentService } from '@/services/store';
import { IPaymentConfigurationRequest } from '@/interfaces/entities/payment-configuration-request.interface';
import { IPaymentConfigurationResponse } from '@/interfaces/entities/payment-configuration-response.interface';
import { ref, readonly, Ref, watch } from 'vue';
import { CardTypes, PaymentTypes } from '@/enums/payment-types.enum';
import { IBSBBranchDetails } from '@/interfaces/entities/bsb-branch-details.interface';
import { IDirectDebitDetails } from '@/interfaces/payment/direct-debit-details';
import { v4 } from 'uuid';
import { ICardDetails } from '@/interfaces';
import { IQConfigSettings, SettingCategory, useConfigSettingsStore } from '@/store';
import { PaymentFrequency } from '@/enums/payment-frequency';
import { jsEncode } from '@/utils/common-helper';

export const usePaymentStore = defineStore('Payment', () => {
  const configSettingsStore = useConfigSettingsStore();

  const reference = ref<string>('');
  const amount = ref<string>('');
  const buttonText = ref<string>('');
  const tokenizeOnly = ref<boolean>(true);
  const googlePay = ref<boolean>(false);
  const bsbBranchDetails = ref<IBSBBranchDetails | null>(null);
  const submitPaymentCallback = ref<((type: PaymentTypes) => void) | null>(null);
  const disablePaymentPanel = ref(false);
  const paymentRetryAttempts = ref<number>(0);
  const maxRetries = ref<number>(3);
  const termsAndConditionsAccepted = ref(false);
  // It would be ideal to have all consumers of PaymentSection component use termsAndConditionsAccepted in this store then this would not be necessary.
  const applyTermsAndConditionsValidationFix = ref(false);

  // Fatzebra POST submit states
  const cardDetailsRef: Ref<ICardDetails | null> = ref(null);
  const directDebitDetails: Ref<IDirectDebitDetails | null> = ref(null);
  const selectedPaymentMethod = ref<PaymentTypes | null>();
  const selectedPaymentFrequency = ref<PaymentFrequency | null>(PaymentFrequency.Monthly);
  const selectedPaymentFrequencyDisplayText = ref<string>();
  const selectedRenewalPreference = ref<boolean>();
  const submissionSuccessful = ref<boolean>();

  const paymentFrequencyWording = ref<{ annual: ''; monthly: '' }>();

  function changePaymentMethod(paymentMethod: PaymentTypes) {
    selectedPaymentMethod.value = paymentMethod;
    if (paymentMethod === PaymentTypes.CreditCard) {
      bsbBranchDetails.value = null;
    }
  }

  watch(
    () => selectedPaymentFrequencyDisplayText.value,
    (newValue: any) => {
      selectedPaymentFrequency.value =
        newValue == paymentFrequencyWording.value?.annual ? PaymentFrequency.Annually : PaymentFrequency.Monthly;
    }
  );

  async function setup(config?: {
    amount?: string;
    buttonText?: string;
    reference?: string;
    callback?: (type: PaymentTypes) => void;
  }) {
    amount.value = config?.amount ?? '0';
    buttonText.value = config?.buttonText ?? 'Submit Payment';
    reference.value = config?.reference ?? v4();
    submitPaymentCallback.value = config?.callback ?? submitPaymentCallback.value;

    const submissionRetries = (await configSettingsStore.getSetting(
      IQConfigSettings.PaymentSubmissionRetries,
      SettingCategory.IQSettings
    )) as number;
    if (submissionRetries) {
      maxRetries.value = submissionRetries;
    }
  }

  function mapToCardDetails(obj: any): ICardDetails {
    const arrCardExpiry = obj.card_expiry.split('/'); //The value will be always be in MM/yyyy format
    return {
      cardType: obj.card_type,
      cardHoldersName: obj.card_holder,
      cardExpiryMonth: arrCardExpiry[0],
      cardExpiryYear: arrCardExpiry[1],
      token: obj.token,
      maskedCardNumber: obj.card_number,
    };
  }

  async function setDirectDebitDetails(accountName: string, bsbNumber: string, accountNumber: string) {
    const branch = await _paymentService.bsbLookup(bsbNumber);
    directDebitDetails.value = <IDirectDebitDetails>{};
    directDebitDetails.value.nameOfAccount = accountName;
    directDebitDetails.value.bankStateBranchNumber = bsbNumber;
    directDebitDetails.value.financialInstitutionName = branch ? jsEncode(branch.name) : '';
    directDebitDetails.value.accountNumber = accountNumber;
  }

  async function getPaymentIframeUrl(): Promise<IPaymentConfigurationResponse | null> {
    try {
      const paymentConfigurationPayload: IPaymentConfigurationRequest = {
        reference: reference.value,
        amount: amount.value,
        buttonText: buttonText.value,
        tokenizeOnly: tokenizeOnly.value,
        googlePay: googlePay.value,
      };

      const paymentConfiguration = await _paymentService.getPaymentIframeConfiguration(paymentConfigurationPayload);
      if (paymentConfiguration) {
        return paymentConfiguration;
      }
    } catch (error) {
      return null;
    }
    return null;
  }

  /**
   * Fz credit card tokenization complete
   *
   * @param payloadResponseData
   */
  function setFzTransactionState(payloadResponseData: object | null) {
    cardDetailsRef.value = payloadResponseData ? mapToCardDetails(payloadResponseData) : null;
    try {
      submitPaymentCallback.value && submitPaymentCallback.value(PaymentTypes.CreditCard);
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Google Pay tokenization complete
   *
   * @param token Google Pay wallet token (raw ECv2 JSON string)
   */
  function setGooglePayToken(token: string) {
    cardDetailsRef.value = {
      cardType: CardTypes.GooglePay,
      cardHoldersName: '',
      cardExpiryMonth: 0,
      cardExpiryYear: 0,
      token,
      maskedCardNumber: '',
    } as ICardDetails;

    try {
      submitPaymentCallback.value && submitPaymentCallback.value(PaymentTypes.GooglePay);
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Apple Pay tokenization complete
   *
   * @param token Apple Pay wallet token (raw JSON string of PKPaymentToken or similar)
   */
  function setApplePayToken(token: string) {
    cardDetailsRef.value = {
      cardType: CardTypes.ApplePay,
      cardHoldersName: '',
      cardExpiryMonth: 0,
      cardExpiryYear: 0,
      token,
      maskedCardNumber: '',
    } as ICardDetails;

    try {
      submitPaymentCallback.value && submitPaymentCallback.value(PaymentTypes.ApplePay);
    } catch (e) {
      console.error(e);
    }
  }

  /**
   * Validate BSB
   *
   * @param bsb
   */
  async function bsbLookup(bsb: string) {
    const res = await _paymentService.bsbLookup(bsb);
    bsbBranchDetails.value = res;
    return !!res;
  }

  async function getApttusDirectDebitStatus(apttusId: string) {
    return await _paymentService.getApttusDirectDebitStatus(apttusId);
  }

  /**
   * Reset Fz IFrame
   */
  function resetIframe() {
    reference.value = v4();
  }

  /**
   * Increment payment attempt
   */
  function incrementPaymentAttempt() {
    paymentRetryAttempts.value = paymentRetryAttempts.value + 1;
  }

  /**
   * Increment payment attempt
   */
  function hasExceededMaxPaymentAttempts() {
    return paymentRetryAttempts.value >= maxRetries.value;
  }

  /**
   *
   * @returns Returns the selected payment method as a string
   */
  function getPaymentMethodAsString(): string {
    if (!selectedPaymentMethod.value) {
      return 'n/a';
    }

    switch (selectedPaymentMethod.value) {
      case PaymentTypes.CreditCard:
        return 'Credit Card';
      case PaymentTypes.GooglePay:
        return 'Google Pay';
      case PaymentTypes.ApplePay:
        return 'Apple Pay';
      case PaymentTypes.BankAccount:
        return 'Bank Account';
      case PaymentTypes.PayLater:
        return 'Pay Later';
      case PaymentTypes.PayPal:
        return 'PayPal';
      default:
        return 'n/a';
    }
  }

  /**
   * Returns the default selected payment method based on the accepted payment types, keeps UI ordering priority from PaymentSection.
   */
  function getDefaultSelectedPaymentMethod(acceptedPaymentTypes: any[]): PaymentTypes | null {
    // This is the priority list from the UI, CreditCard is listed first so it will be the the selected payment method if in the acceptedPaymentTypes
    const paymentPriority: (keyof typeof PaymentTypes)[] = [
      'CreditCard',
      'BankAccount',
      'GooglePay',
      'ApplePay',
      'PayPal',
      'PayLater',
    ];

    for (const priority of paymentPriority) {
      const match = acceptedPaymentTypes.find((a) => a.name === priority);

      if (match) {
        return PaymentTypes[priority];
      }
    }

    return null;
  }

  return {
    reference,
    amount,
    buttonText,
    tokenizeOnly,
    googlePay,
    selectedPaymentMethod,
    selectedPaymentFrequency,
    selectedPaymentFrequencyDisplayText,
    selectedRenewalPreference,
    paymentFrequencyWording,
    getPaymentIframeUrl,
    setFzTransactionState,
    setGooglePayToken,
    setApplePayToken,
    changePaymentMethod,
    bsbLookup,
    submitPaymentCallback,
    disablePaymentPanel,
    termsAndConditionsAccepted,
    applyTermsAndConditionsValidationFix,
    cardDetails: readonly(cardDetailsRef),
    setDirectDebitDetails,
    directDebitDetails,
    setup,
    resetIframe,
    paymentRetryAttempts,
    maxRetries,
    incrementPaymentAttempt,
    hasExceededMaxPaymentAttempts,
    bsbBranchDetails,
    submissionSuccessful,
    getApttusDirectDebitStatus,
    getPaymentMethodAsString,
    getDefaultSelectedPaymentMethod,
  };
});
